import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver } from "../../actions/valueActions";

import { formatString, convertBoolDiffValue } from "./valueFunctions";
import { getIconFromDriverName } from "./iconFunctions";
import RevenueValueDriverMarketingForm from "./RevenueValueDriverMarketingForm";
import { getAllOutcomes } from "./valueFunctions";

const RevenueValueDriverMarketing = ({ id, valueModel, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    const [editMarketingDriverId, setEditMarketingDriverId] = useState(null);
    const [deleteMarketingDriverPopupOpen, setDeleteMarketingDriverPopupOpen] = useState(false);
    const [deleteMarketingDriverId, setDeleteMarketingDriverId] = useState(null);

    const handleResetMarketing = () => {
        setEditMarketingDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const openDeleteMarketingDriver = (id) => {
        setDeleteMarketingDriverId(id)
        setDeleteMarketingDriverPopupOpen(true)
    }

    const editMarketingDriver = (id) => {
        setEditMarketingDriverId(id)
    }

    const onSubmitMarketing = async (values, driver, { setSubmitting, setErrors }) => {
        values.id = editMarketingDriverId
        if (outcome) {
            values.outcome = outcome
        }
        values.driver = driver
        values.annual_contract_value = values.annual_contract_value.toString().replace(/,/g, "");
        values.transaction_value = values.transaction_value.toString().replace(/,/g, "");
        values.customer_acquisition_costs = values.customer_acquisition_costs.toString().replace(/,/g, "");
        let success = false

        if (editMarketingDriverId) {
            const result = await dispatch(editDriver('marketing', id, values, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditMarketingDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver('marketing', id, values, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const marketingDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        { id: 'cac_decrease', label: 'CAC change', minWidth: 150, action: false },
        { id: 'revenue_model', label: 'Revenue model', format: (value) => formatString(value), minWidth: 100, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createMarketingDriverData = (id, outcome, driver_name, cac_decrease, revenue_model, positive_driver, actions) => {
        cac_decrease = `${cac_decrease}% ${positive_driver ? 'decrease': 'increase'}`
        return { id, outcome, driver_name, cac_decrease, revenue_model, positive_driver, actions };
    }
    
    const marketingDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editMarketingDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteMarketingDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }
    
    const marketingDriverRows = valueModel?.revenue_value_driver.marketing_driven.filter(driver => !outcome || driver.outcome === outcome).map((driver) => {
        return (
            createMarketingDriverData(driver.id, driver.outcome, driver.driver_name, driver.cac_decrease, driver.revenue_model, driver.positive_driver, marketingDriverActions(driver.id))
        )
    })
    
    const marketingDriverTable = () => {
        return (
            <React.Fragment>
                <Divider />
                <Table
                    columns={marketingDriverColumns}
                    rows={marketingDriverRows}
                    selectedId={editMarketingDriverId}
                />
            </React.Fragment>
        )
    }

    const marketingDriverFormActions = () => {
        return (
            <React.Fragment>
                {editMarketingDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editMarketingDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetMarketing}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Driver type:
                </Typography>
                <Typography variant="caption">
                    Marketing and Sales
                </Typography>
            </Stack>
            <RevenueValueDriverMarketingForm
                currency="€"
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                editMarketingDriverId={editMarketingDriverId}
                editMarketingDriver={valueModel?.revenue_value_driver.marketing_driven.find(marketingDriver => marketingDriver.id === editMarketingDriverId)}
                formActions={marketingDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitMarketing(values, driver, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {marketingDriverTable()}
            <DeletePopup open={deleteMarketingDriverPopupOpen} onClose={() => setDeleteMarketingDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('marketing', id, deleteMarketingDriverId))} />
        </Box>
    )
    
}

export default RevenueValueDriverMarketing